import { useState, useEffect, useMemo } from 'react'
import { Folder, FileText } from 'lucide-react'
import { api } from '../api/client'
import { useEntities } from '../context/EntitiesContext'
import type { IAPIExecutionDailyStat } from 'extra/types/ApiModels'

type TimePeriod = 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth'

function getDateRange (period: TimePeriod): { start: Date; end: Date } {
  const now = new Date()
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))

  switch (period) {
    case 'today': {
      const end = new Date(today)
      end.setUTCDate(end.getUTCDate() + 1)
      return { start: today, end }
    }
    case 'yesterday': {
      const start = new Date(today)
      start.setUTCDate(start.getUTCDate() - 1)
      return { start, end: today }
    }
    case 'thisWeek': {
      const start = new Date(today)
      const dayOfWeek = start.getUTCDay()
      const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1
      start.setUTCDate(start.getUTCDate() - mondayOffset)
      const end = new Date(today)
      end.setUTCDate(end.getUTCDate() + 1)
      return { start, end }
    }
    case 'lastWeek': {
      const thisMonday = new Date(today)
      const dayOfWeek = thisMonday.getUTCDay()
      const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1
      thisMonday.setUTCDate(thisMonday.getUTCDate() - mondayOffset)
      const start = new Date(thisMonday)
      start.setUTCDate(start.getUTCDate() - 7)
      return { start, end: thisMonday }
    }
    case 'thisMonth': {
      const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
      const end = new Date(today)
      end.setUTCDate(end.getUTCDate() + 1)
      return { start, end }
    }
    case 'lastMonth': {
      const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1))
      const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
      return { start, end }
    }
  }
}

const utcDateFmt = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
const utcShortFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })

function getPeriodDescription (period: TimePeriod): { text: string; isPartial: boolean } {
  const { start, end } = getDateRange(period)
  const isPartial = period === 'today' || period === 'thisWeek' || period === 'thisMonth'

  if (period === 'today' || period === 'yesterday') {
    return { text: utcDateFmt.format(start), isPartial }
  }

  const lastDay = new Date(end)
  lastDay.setUTCDate(lastDay.getUTCDate() - 1)
  return { text: `${utcShortFmt.format(start)} – ${utcShortFmt.format(lastDay)}`, isPartial }
}

function filterByPeriod (stats: Array<IAPIExecutionDailyStat>, period: TimePeriod): Array<IAPIExecutionDailyStat> {
  const { start, end } = getDateRange(period)
  return stats.filter(s => {
    const d = new Date(s.date)
    return d >= start && d < end
  })
}

interface Aggregated {
  executionCount: number
  errorCount: number
  avgLatency: number | null
  totalInputTokens: number
  totalOutputTokens: number
  totalThinkingTokens: number
}

function aggregate (stats: Array<IAPIExecutionDailyStat>): Aggregated {
  const totalExec = stats.reduce((sum, s) => sum + s.executionCount, 0)
  const totalErrors = stats.reduce((sum, s) => sum + s.errorCount, 0)
  const totalInput = stats.reduce((sum, s) => sum + s.totalInputTokens, 0)
  const totalOutput = stats.reduce((sum, s) => sum + s.totalOutputTokens, 0)
  const totalThinking = stats.reduce((sum, s) => sum + s.totalThinkingTokens, 0)

  let avgLatency: number | null = null
  const latencySum = stats.reduce((sum, s) => sum + (s.avgLatency ?? 0) * s.executionCount, 0)
  if (totalExec > 0) {
    avgLatency = latencySum / totalExec
  }

  return { executionCount: totalExec, errorCount: totalErrors, avgLatency, totalInputTokens: totalInput, totalOutputTokens: totalOutput, totalThinkingTokens: totalThinking }
}

interface ModelRow {
  model: string
  agg: Aggregated
}

interface PromptNode {
  promptId: string | null
  promptName: string
  isDeleted: boolean
  agg: Aggregated
  models: Array<ModelRow>
}

interface FolderNode {
  folderId: string
  folderName: string
  agg: Aggregated
  prompts: Array<PromptNode>
}

function formatNumber (n: number): string {
  return n.toLocaleString()
}

function formatLatency (ms: number | null): string {
  if (ms === null) return '-'
  if (ms < 1000) return `${Math.round(ms)}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

function formatPercent (n: number, total: number): string {
  if (total === 0) return '0%'
  return `${((n / total) * 100).toFixed(1)}%`
}

function StatCells ({ agg, className }: { agg: Aggregated; className?: string }) {
  const cls = className ?? 'text-gray-300'
  return (
    <>
      <td className={`py-1.5 px-3 text-right ${cls}`}>{formatNumber(agg.executionCount)}</td>
      <td className={`py-1.5 px-3 text-right ${cls}`}>{formatNumber(agg.errorCount)}</td>
      <td className={`py-1.5 px-3 text-right ${cls}`}>{formatPercent(agg.errorCount, agg.executionCount)}</td>
      <td className={`py-1.5 px-3 text-right ${cls}`}>{formatLatency(agg.avgLatency)}</td>
      <td className={`py-1.5 px-3 text-right ${cls}`}>{formatNumber(agg.totalInputTokens)}{agg.executionCount > 0 && <span className="text-gray-500"> (avg. {formatNumber(Math.round(agg.totalInputTokens / agg.executionCount))})</span>}</td>
      <td className={`py-1.5 px-3 text-right ${cls}`}>{formatNumber(agg.totalOutputTokens)}{agg.executionCount > 0 && <span className="text-gray-500"> (avg. {formatNumber(Math.round(agg.totalOutputTokens / agg.executionCount))})</span>}</td>
    </>
  )
}

export function ExecutionStatsPage () {
  const [stats, setStats] = useState<Array<IAPIExecutionDailyStat>>([])
  const [loading, setLoading] = useState(true)
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('today')
  const { prompts, folders } = useEntities()

  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.executions.stats()
        setStats(data)
      } catch (err) {
        console.error('Failed to load stats:', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const filteredStats = useMemo(() => filterByPeriod(stats, timePeriod), [stats, timePeriod])
  const overall = useMemo(() => aggregate(filteredStats), [filteredStats])

  const promptMap = useMemo(() => {
    const map = new Map<string, { name: string; folderId: string | null }>()
    for (const p of prompts) {
      map.set(p.id, { name: p.name, folderId: p.folderId })
    }
    return map
  }, [prompts])

  const folderMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const f of folders) {
      map.set(f.id, f.name)
    }
    return map
  }, [folders])

  // Build hierarchy: folder → prompt → model
  const hierarchy = useMemo((): Array<FolderNode> => {
    // Group stats by (folderId, promptId, model)
    // First, determine folder for each stat row
    const folderBuckets = new Map<string, Map<string, Array<IAPIExecutionDailyStat>>>()

    for (const s of filteredStats) {
      const promptInfo = s.promptId ? promptMap.get(s.promptId) : null
      const isDeleted = s.promptId !== null && !promptInfo

      let folderKey: string
      if (isDeleted) {
        folderKey = '__deleted__'
      } else if (promptInfo?.folderId) {
        folderKey = promptInfo.folderId
      } else {
        folderKey = '__uncategorized__'
      }

      const promptKey = s.promptId ?? '__null__'

      if (!folderBuckets.has(folderKey)) {
        folderBuckets.set(folderKey, new Map())
      }
      const promptBuckets = folderBuckets.get(folderKey)!
      if (!promptBuckets.has(promptKey)) {
        promptBuckets.set(promptKey, [])
      }
      promptBuckets.get(promptKey)!.push(s)
    }

    const result: Array<FolderNode> = []

    for (const [folderKey, promptBuckets] of folderBuckets) {
      let folderName: string
      if (folderKey === '__deleted__') {
        folderName = 'Deleted Prompts'
      } else if (folderKey === '__uncategorized__') {
        folderName = 'Uncategorized'
      } else {
        folderName = folderMap.get(folderKey) ?? 'Unknown Folder'
      }

      const promptNodes: Array<PromptNode> = []
      const allFolderStats: Array<IAPIExecutionDailyStat> = []

      for (const [promptKey, statRows] of promptBuckets) {
        allFolderStats.push(...statRows)

        const promptInfo = promptKey !== '__null__' ? promptMap.get(promptKey) : null
        const isDeleted = folderKey === '__deleted__'
        const promptName = promptInfo?.name ?? statRows[0]?.promptName ?? 'Unknown'

        // Group by model within this prompt
        const modelBuckets = new Map<string, Array<IAPIExecutionDailyStat>>()
        for (const row of statRows) {
          const modelKey = row.model ?? 'Unknown'
          if (!modelBuckets.has(modelKey)) {
            modelBuckets.set(modelKey, [])
          }
          modelBuckets.get(modelKey)!.push(row)
        }

        const models: Array<ModelRow> = []
        for (const [model, modelStats] of modelBuckets) {
          models.push({ model, agg: aggregate(modelStats) })
        }
        models.sort((a, b) => b.agg.executionCount - a.agg.executionCount)

        promptNodes.push({
          promptId: promptKey === '__null__' ? null : promptKey,
          promptName,
          isDeleted,
          agg: aggregate(statRows),
          models,
        })
      }

      promptNodes.sort((a, b) => b.agg.executionCount - a.agg.executionCount)

      result.push({
        folderId: folderKey,
        folderName,
        agg: aggregate(allFolderStats),
        prompts: promptNodes,
      })
    }

    result.sort((a, b) => b.agg.executionCount - a.agg.executionCount)
    return result
  }, [filteredStats, promptMap, folderMap])

  if (loading) {
    return (
      <div>
        <h1 className="text-lg font-bold text-white mb-4">Execution Stats</h1>
        <div className="text-gray-400 text-sm">Loading stats...</div>
      </div>
    )
  }

  return (
    <div>
      {/* Header: title + time period + description */}
      <div className="flex items-center gap-4 mb-1">
        <h1 className="text-lg font-bold text-white">Execution Stats</h1>
        <div className="flex gap-1">
          {([
            ['today', 'Today'],
            ['yesterday', 'Yesterday'],
            ['thisWeek', 'This Week'],
            ['lastWeek', 'Last Week'],
            ['thisMonth', 'This Month'],
            ['lastMonth', 'Last Month'],
          ] as Array<[TimePeriod, string]>).map(([value, label]) => (
            <button
              key={value}
              onClick={() => setTimePeriod(value)}
              className={`px-2 py-1 rounded text-xs transition-colors ${
                timePeriod === value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="text-xs text-gray-400 mb-4">
        {getPeriodDescription(timePeriod).text} (UTC)
        {getPeriodDescription(timePeriod).isPartial && (
          <span className="text-yellow-500 ml-2">Data is partial — period is still in progress</span>
        )}
      </div>

      {/* Compact overall summary */}
      <div className="flex gap-6 mb-4 text-sm">
        <div>
          <span className="text-gray-500">Executions </span>
          <span className="text-white font-medium">{formatNumber(overall.executionCount)}</span>
        </div>
        <div>
          <span className="text-gray-500">Error Rate </span>
          <span className="text-white font-medium">{formatPercent(overall.errorCount, overall.executionCount)}</span>
        </div>
        <div>
          <span className="text-gray-500">Avg Latency </span>
          <span className="text-white font-medium">{formatLatency(overall.avgLatency)}</span>
        </div>
        <div>
          <span className="text-gray-500">Tokens </span>
          <span className="text-white font-medium">{formatNumber(overall.totalInputTokens + overall.totalOutputTokens + overall.totalThinkingTokens)}</span>
        </div>
      </div>

      {/* Hierarchical table */}
      {hierarchy.length > 0
        ? (
        <div className="rounded-lg overflow-hidden border border-gray-700">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-900 border-b border-gray-700">
                <th className="text-left py-2 px-3 text-gray-400 font-medium text-xs">Name</th>
                <th className="text-right py-2 px-3 text-gray-400 font-medium text-xs">Executions</th>
                <th className="text-right py-2 px-3 text-gray-400 font-medium text-xs">Errors</th>
                <th className="text-right py-2 px-3 text-gray-400 font-medium text-xs">Error %</th>
                <th className="text-right py-2 px-3 text-gray-400 font-medium text-xs">Avg Latency</th>
                <th className="text-right py-2 px-3 text-gray-400 font-medium text-xs">Input Tokens</th>
                <th className="text-right py-2 px-3 text-gray-400 font-medium text-xs">Output Tokens</th>
              </tr>
            </thead>
            <tbody>
              {hierarchy.map(folder => (
                <FolderSection key={folder.folderId} folder={folder} />
              ))}
            </tbody>
          </table>
        </div>
          )
        : (
        <div className="text-gray-400 text-center py-8 text-sm">No data for the selected period.</div>
          )}
    </div>
  )
}

function FolderSection ({ folder }: { folder: FolderNode }) {
  return (
    <>
      {/* Folder row - darkest background */}
      <tr className="bg-gray-800 border-b border-gray-700">
        <td className="py-1.5 px-3 text-white font-semibold text-xs uppercase tracking-wide">
          <span className="inline-flex items-center gap-1.5"><Folder className="h-3.5 w-3.5 flex-shrink-0" />{folder.folderName}</span>
        </td>
        <StatCells agg={folder.agg} className="text-gray-300 font-medium" />
      </tr>
      {folder.prompts.map(prompt => (
        <PromptSection key={prompt.promptId ?? '__null__'} prompt={prompt} />
      ))}
    </>
  )
}

function PromptSection ({ prompt }: { prompt: PromptNode }) {
  return (
    <>
      {/* Prompt row - medium background */}
      <tr className="bg-gray-750 border-b border-gray-700/50" style={{ backgroundColor: 'rgb(42, 48, 60)' }}>
        <td className={`py-1.5 pl-6 pr-3 ${prompt.isDeleted ? 'text-gray-400 italic' : 'text-gray-200'}`}>
          <span className="inline-flex items-center gap-1.5"><FileText className="h-3.5 w-3.5 flex-shrink-0" />{prompt.promptName}</span>
        </td>
        <StatCells agg={prompt.agg} className={prompt.isDeleted ? 'text-gray-500' : 'text-gray-300'} />
      </tr>
      {/* Model rows - lightest background */}
      {prompt.models.map(model => (
        <tr key={model.model} className="border-b border-gray-700/30" style={{ backgroundColor: 'rgb(48, 54, 68)' }}>
          <td className="py-1 pl-10 pr-3 text-gray-400 text-xs">{model.model}</td>
          <StatCells agg={model.agg} className="text-gray-400 text-xs" />
        </tr>
      ))}
    </>
  )
}
